import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  signOut,
  UserCredential,
  getAuth,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import { auth } from "../core/persistence";
import FirestoreService from "../firestore";
import {
  AuthService,
  LoginFormData,
  CreateAccountParams,
  InviteAccountParams,
  UserClaims,
  ParticipationFormData,
  FirebaseInvitation,
} from "../../types/auth/auth.types";
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc, Timestamp } from "firebase/firestore";
import { db } from "../../_db";

class AuthServiceImpl implements AuthService {
  async login(data: LoginFormData): Promise<UserCredential | null> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, data.email, data.password);

      // After successful login, check and update company registration if needed
      try {
        const idTokenResult = await userCredential.user.getIdTokenResult();
        const claims = idTokenResult.claims as unknown as UserClaims;
        if (claims.role === "company" && claims.companyId) {
          await this.checkAndUpdateCompanyRegistration(claims.companyId);
        }
      } catch (error) {
        console.error("Error checking company registration:", error instanceof Error ? error.message : "Unknown error");
        // Don't throw here - the user is still logged in successfully
      }

      return userCredential;
    } catch (error) {
      console.error("Login failed:", error instanceof Error ? error.message : "Unknown error");
      return null;
    }
  }

  async checkAndUpdateCompanyRegistration(companyId: string): Promise<void> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error("No user is currently signed in");
        return;
      }

      // Call the backend to mark the company as registered
      await FirestoreService.request(
        "manage-company-account",
        {
          companyId,
          type: "mark-registered",
        },
        "companies",
        currentUser.uid
      );
    } catch (error) {
      console.error(
        "Error updating company registration status:",
        error instanceof Error ? error.message : "Unknown error"
      );
      // Don't throw - this is a non-critical operation
    }
  }

  async resetPassword(email: string): Promise<boolean> {
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      console.error("Error sending password reset email:", error);
      return false;
    }
  }

  async logout(): Promise<void> {
    return signOut(auth);
  }

  async setPersistenceType(rememberMe: boolean): Promise<void> {
    const persistenceType = rememberMe ? browserLocalPersistence : browserSessionPersistence;
    return setPersistence(auth, persistenceType);
  }

  async createCompanyAccount(params: CreateAccountParams) {
    try {
      const result = await FirestoreService.request(
        "manage-company-account",
        {
          companyId: params.companyId,
          email: params.email,
          type: "create-account",
        },
        "companies"
      );
      return result;
    } catch (error) {
      console.error("Error creating company account:", error);
      throw error;
    }
  }

  async updateCompanyEmail(companyId: string, newEmail: string) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No user is currently signed in");
      }

      // Then update the email in the backend (which will handle the old user deletion)
      const result = await FirestoreService.request(
        "manage-company-account",
        {
          companyId,
          newEmail,
          type: "update-email",
          deleteUser: true, // Flag to indicate we want to delete the old user document
        },
        "companies",
        currentUser.uid
      );

      return result;
    } catch (error) {
      console.error("Error updating company email:", error);
      throw error;
    }
  }

  async deleteCompanyEmail(companyId: string) {
    try {
      const result = await FirestoreService.request(
        "manage-company-account",
        {
          companyId,
          type: "delete-account",
        },
        "companies"
      );
      return result;
    } catch (error) {
      console.error("Error deleting company email:", error);
      throw error;
    }
  }

  async revokeCompanyAccess(companyId: string, email: string) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No user is currently signed in");
      }

      // Revoke access for the specified email by sending a request to the backend
      const result = await FirestoreService.request(
        "manage-company-account",
        {
          companyId,
          email,
          type: "revoke-access",
        },
        "companies",
        currentUser.uid
      );

      return result;
    } catch (error) {
      console.error("Error revoking company access:", error);
      throw error;
    }
  }

  async invalidateSpecificInvitation(companyId: string, oldEmail: string, newEmail: string) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No user is currently signed in");
      }

      try {
        const result = await FirestoreService.request(
          "manage-company-account",
          {
            companyId,
            oldEmail,
            newEmail,
            type: "invalidate-specific-invitation",
          },
          "companies",
          currentUser.uid
        );
        return result;
      } catch (backendError) {
        console.warn("Backend invalidation not available:", backendError);
        return { success: true };
      }
    } catch (error) {
      console.error("Error invalidating specific invitation:", error);
      throw error;
    }
  }

  async invalidateAllCompanyInvitations(companyId: string) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No user is currently signed in");
      }

      const invitationsRef = collection(db, "invitations");
      const q = query(
        invitationsRef,
        where("companyId", "==", companyId)
      );
      
      const querySnapshot = await getDocs(q);
      
      for (const docSnapshot of querySnapshot.docs) {
        await deleteDoc(doc(db, "invitations", docSnapshot.id));
      }
      
      if (querySnapshot.docs.length > 0) {
        console.log(`Deleted ${querySnapshot.docs.length} existing invitation(s) for company: ${companyId}`);
      }

      try {
        const result = await FirestoreService.request(
          "manage-company-account",
          {
            companyId,
            type: "invalidate-all-invitations",
          },
          "companies",
          currentUser.uid
        );
        return result;
      } catch (backendError) {
        console.warn("Backend invalidation not available:", backendError);
        return { success: true };
      }
    } catch (error) {
      console.error("Error invalidating company invitations:", error);
      throw error;
    }
  }

  async validateInvitation(email: string): Promise<{ isValid: boolean; error?: string }> {
    try {
      const invitationsRef = collection(db, "invitations");
      const q = query(
        invitationsRef,
        where("email", "==", email),
        where("status", "in", ["pending", "used"]) // Only check pending and used invitations
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return { isValid: false, error: "invitation_not_found" };
      }
      
      const invitationDoc = querySnapshot.docs[0];
      const invitation = {
        id: invitationDoc.id,
        ...invitationDoc.data()
      } as FirebaseInvitation;
      
      // Check if the invitation has expired
      const expiresAt = invitation.expiresAt instanceof Timestamp ? 
        invitation.expiresAt.toDate() : new Date(invitation.expiresAt);
      
      if (new Date() > expiresAt) {
        // Mark as expired
        await this.markInvitationAsExpired(invitation.id);
        return { isValid: false, error: "invitation_expired" };
      }
      
      // Check if the invitation has been used
      if (invitation.used || invitation.status === "used") {
        return { isValid: false, error: "invitation_used" };
      }
      
      // Check if the invitation has been superseded
      if (invitation.status === "superseded") {
        return { isValid: false, error: "invitation_superseded" };
      }
      
      // Check if the invitation has been revoked
      if (invitation.status === "revoked") {
        return { isValid: false, error: "invitation_revoked" };
      }
      
      return { isValid: true };
      
    } catch (error) {
      console.error("Error validating invitation:", error);
      return { isValid: true };
    }
  }

  private async markInvitationAsExpired(invitationId: string): Promise<void> {
    try {
      const invitationRef = doc(db, "invitations", invitationId);
      await updateDoc(invitationRef, {
        status: "expired",
        updatedAt: new Date()
      });
    } catch (error) {
      console.error("Error marking invitation as expired:", error);
    }
  }




  async sendAccountInvitation(params: InviteAccountParams) {
    try {
      const auth = getAuth();

      // Get the current user's ID
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No user is currently signed in");
      }

      try {
        const checkResult = (await FirestoreService.request(
          "manage-company-account",
          {
            companyId: params.companyId,
            type: "check-account-exists",
            email: params.email,
          },
          "companies",
          currentUser.uid
        )) as { entity: { exists: boolean } };

        // If the account already exists (this is a resend), we don't need to create it again
        const isResend = checkResult && checkResult.entity.exists === true;

        // Store email for sign-in process
        localStorage.setItem("emailForSignIn", params.email);

        // Get current user info for the invitation
        const invitedByName = currentUser.displayName || currentUser.email || "Sistema ASSIMAGRA";

        // Delete existing invitations for this company before creating a new one
        await this.invalidateAllCompanyInvitations(params.companyId);

        // Send our custom branded email with proper Firebase Auth link (generated by backend)
        await FirestoreService.call("sendInvitationEmail", {
          companyName: params.companyName,
          contactName: params.email.split("@")[0], // Use email prefix as contact name if no name provided
          email: params.email,
          companyId: params.companyId,
          invitedBy: invitedByName,
          baseUrl: window.location.origin,
        });

        return { success: true };
      } catch (error) {
        console.error("Error sending company account invitation:", error);
        throw error;
      }
    } catch (error) {
      console.error("Error sending company account invitation:", error);
      throw error;
    }
  }



  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const user = auth.currentUser;
    if (!user || !user.email) {
      throw new Error("No user is currently signed in");
    }

    try {
      // Create credentials with email and current password
      const credential = EmailAuthProvider.credential(user.email, currentPassword);

      // Create a custom error handler to avoid raw Firebase errors being displayed
      // This will wrap the reauthentication in a try-catch to catch and transform Firebase errors
      const safeReauthenticate = async () => {
        try {
          // Attempt to reauthenticate with current password
          return await reauthenticateWithCredential(user, credential);
        } catch (error: any) {
          // If wrong credentials, throw our custom error instead of Firebase's
          if (error.code === "auth/wrong-password" || error.code === "auth/invalid-credential") {
            throw new Error("Current password is incorrect");
          }
          // For other errors, transform to our custom format
          throw new Error("Authentication failed. Please try again.");
        }
      };

      // Safely reauthenticate
      await safeReauthenticate();

      // Only if reauthentication succeeds, update the password
      try {
        await updatePassword(user, newPassword);
      } catch (error: any) {
        // Transform password update errors
        if (error.code === "auth/weak-password") {
          throw new Error("New password is too weak. Please use a stronger password.");
        }
        throw new Error("Failed to update password. Please try again.");
      }
    } catch (error: any) {
      // At this point, all Firebase errors should be transformed to our custom errors
      throw error;
    }
  }

  async sendParticipationForm(params: ParticipationFormData) {
    try {
      console.log("Sending participation form:", params);
      await FirestoreService.call("sendParticipationEmail", params);
      return true;
    } catch (error) {
      console.error("Error sending participation form:", error);
      throw error;
    }
  }

  async markInvitationAsUsed(email: string): Promise<void> {
    try {
      const invitationsRef = collection(db, "invitations");
      const q = query(
        invitationsRef,
        where("email", "==", email),
        where("used", "==", false)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const invitationDoc = querySnapshot.docs[0];
        
        // Mark it as used
        const invitationRef = doc(db, "invitations", invitationDoc.id);
        await updateDoc(invitationRef, {
          used: true,
          status: "used",
          updatedAt: new Date()
        });
        
        console.log(`Invitation marked as used for email: ${email}`);
      } else {
        console.warn(`No unused invitation found for email: ${email}`);
      }
    } catch (error) {
      console.error("Error marking invitation as used:", error);
    }
  }

  async getInvitationDetails(email: string): Promise<FirebaseInvitation | null> {
    try {
      const invitationsRef = collection(db, "invitations");
      const q = query(
        invitationsRef,
        where("email", "==", email)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const invitationDoc = querySnapshot.docs[0];
      return {
        id: invitationDoc.id,
        ...invitationDoc.data()
      } as FirebaseInvitation;
    } catch (error) {
      console.error("Error getting invitation details:", error);
      return null;
    }
  }
}

export const authService = new AuthServiceImpl();
